import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Title } from '@angular/platform-browser';
import { environment } from '../../../environments/environment';
import { WINDOW } from '../tokens/window';
import {
  AnalyticsEventParams,
  DataLayerObject,
} from '../analytics/analytics.types';
import {
  PARENT_ANALYTICS_LAYOUT,
  ParentAnalyticsEvents,
} from '../analytics/parent-analytics.events';

/**
 * Thin, reusable analytics facade over `window.dataLayer`.
 * - GTM mode (`gtmContainerId`): dataLayer.push only — map events in GTM.
 * - Direct GA4 mode: dataLayer.push + gtag('event') mirror.
 */
@Injectable({
  providedIn: 'root',
})
export class GaEventService {
  readonly #window = inject(WINDOW);
  readonly #platformId = inject(PLATFORM_ID);
  readonly #title = inject(Title);
  readonly #measurementId = environment.gaMeasurementId?.trim() ?? '';
  readonly #gtmId = environment.gtmContainerId?.trim() ?? '';
  readonly #useGtm = !!this.#gtmId;

  /** Low-level push — preferred when wiring GTM Custom Event tags. */
  push(payload: DataLayerObject): void {
    if (!this.#canTrack()) {
      return;
    }
    this.#window.dataLayer = this.#window.dataLayer ?? [];
    this.#window.dataLayer.push(payload);
  }

  /**
   * Tracks a named event via dataLayer.
   * GTM: create a Custom Event trigger matching `eventName`.
   * GA4-only: also mirrors through gtag when available.
   */
  sendEvent(eventName: string, params: AnalyticsEventParams = {}): void {
    if (!this.#canTrack()) {
      return;
    }

    const cleaned = this.#cleanParams(params);
    this.push({
      event: eventName,
      ...cleaned,
    });

    // Direct GA4 only — GTM owns delivery when container is configured
    if (
      !this.#useGtm &&
      typeof this.#window.gtag === 'function'
    ) {
      this.#window.gtag('event', eventName, cleaned);
    }
  }

  /** Parent SPA virtual page view (call on NavigationEnd). */
  trackPageView(
    path: string,
    extras: AnalyticsEventParams = {},
  ): void {
    if (!this.#canTrack()) {
      return;
    }

    const pagePath = path.split('?')[0] || '/';
    const pageTitle = this.#title.getTitle();
    const payload: AnalyticsEventParams = {
      page_path: pagePath,
      page_location: this.#window.location.href,
      page_title: pageTitle,
      layout: PARENT_ANALYTICS_LAYOUT,
      ...extras,
    };

    this.sendEvent(ParentAnalyticsEvents.PAGE_VIEW, payload);

    // Standard GA4 page_view — needed for stream health / "data collection active"
    if (!this.#useGtm && typeof this.#window.gtag === 'function') {
      this.#window.gtag('event', 'page_view', {
        page_path: pagePath,
        page_location: this.#window.location.href,
        page_title: pageTitle,
        send_to: this.#measurementId,
      });
    }

    if (
      !this.#useGtm &&
      typeof this.#window.gtag === 'function' &&
      this.#measurementId
    ) {
      this.#window.gtag('config', this.#measurementId, {
        page_path: pagePath,
        page_title: pageTitle,
      });
    }
  }

  #canTrack(): boolean {
    if (!isPlatformBrowser(this.#platformId)) {
      return false;
    }
    return !!(this.#measurementId || this.#gtmId);
  }

  #cleanParams(params: AnalyticsEventParams): AnalyticsEventParams {
    return Object.fromEntries(
      Object.entries(params).filter(([, value]) => value !== undefined),
    );
  }
}
